import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

// Browser Web Push subscription manager for Ava (آوا) notifications - the "tab
// closed" channel. Handles permission, fetching the server VAPID public key,
// subscribing via the service worker's pushManager, and registering the
// subscription with the server. iOS gating: push works only inside an installed
// (Add-to-Home-Screen) PWA on iOS 16.4+, never in the Safari tab.

function isPushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}
function isIos() {
  return /iphone|ipad|ipod/i.test((typeof navigator !== 'undefined' && navigator.userAgent) || '');
}
function isStandalone() {
  return (typeof window !== 'undefined'
    && ((window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
      || window.navigator.standalone === true));
}

// VAPID public keys are base64url; pushManager wants a Uint8Array.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function getToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

export function useAvaPush() {
  const supported = isPushSupported();
  const [permission, setPermission] = useState(supported ? Notification.permission : 'unsupported');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // On iOS, push needs an installed PWA; a plain Safari tab cannot subscribe.
  const iosBlocked = supported && isIos() && !isStandalone();

  // Reflect the existing subscription state on mount.
  useEffect(() => {
    if (!supported) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscribed(!!sub);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [supported]);

  const enable = useCallback(async () => {
    if (!supported || busy) return;
    setBusy(true); setError('');
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') { setError('اجازهٔ اعلان داده نشد.'); return; }
      const token = await getToken();
      if (!token) { setError('برای فعال‌سازی باید وارد باشید.'); return; }

      const keyRes = await fetch('/api/ava/push/public-key', { headers: { Authorization: `Bearer ${token}` } });
      const keyJson = await keyRes.json().catch(() => ({}));
      if (!keyRes.ok || !keyJson.publicKey) { setError('سرویس اعلان روی سرور پیکربندی نشده است.'); return; }

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyJson.publicKey),
      });
      const saveRes = await fetch('/api/ava/push/subscribe', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription: sub.toJSON ? sub.toJSON() : sub }),
      });
      if (!saveRes.ok) { setError('ثبت اشتراک اعلان ناموفق بود.'); return; }
      setSubscribed(true);
    } catch (e) {
      setError((e && e.message) || 'فعال‌سازی اعلان ناموفق بود.');
    } finally {
      setBusy(false);
    }
  }, [supported, busy]);

  const disable = useCallback(async () => {
    if (!supported || busy) return;
    setBusy(true); setError('');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const token = await getToken();
        await fetch('/api/ava/push/subscribe', {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        }).catch(() => {});
        await sub.unsubscribe().catch(() => {});
      }
      setSubscribed(false);
    } catch (e) {
      setError((e && e.message) || 'غیرفعال‌سازی اعلان ناموفق بود.');
    } finally {
      setBusy(false);
    }
  }, [supported, busy]);

  return { supported, permission, subscribed, busy, error, iosBlocked, enable, disable };
}
